// removeFilter — VQE-Edit edit-action: drop a single WHERE fragment by
// 0-based fragmentIndex (and the leading WHERE keyword when removing the
// only fragment). Builds an SR-Edit `remove-where` op and delegates to
// `commitEdit`. SR-Edit handles the AND/OR-stripping; this module only
// validates the index against the structure when one is supplied.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
// filter (WHERE / HAVING).

import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import { commitEdit, type CommitEditCtx } from '../commitEdit';

export type RemoveFilterOptions = {
  /**
   * Optional structure used for index-bounds validation. Lets the caller
   * surface a friendlier error than SR-Edit's silent no-op for out-of-range
   * indices.
   */
  readonly structure?: QueryStructure;
  /** Target clause — `'where'` (default) or `'having'`. */
  readonly clause?: BooleanClauseTarget;
};

export async function removeFilter(
  fragmentIndex: number,
  ctx: CommitEditCtx,
  options: RemoveFilterOptions = {},
): Promise<void> {
  if (!Number.isInteger(fragmentIndex) || fragmentIndex < 0) {
    throw new Error(
      `removeFilter: fragmentIndex must be a non-negative integer (got ${fragmentIndex})`,
    );
  }
  const clause = options.clause ?? 'where';
  const label = clause === 'having' ? 'HAVING' : 'WHERE';
  if (options.structure) {
    const fragments = (clause === 'having' ? options.structure.having : options.structure.where) ?? [];
    if (fragments.length === 0) {
      throw new Error(`removeFilter: no ${label} clause to remove from`);
    }
    if (fragmentIndex >= fragments.length) {
      throw new Error(
        `removeFilter: fragmentIndex ${fragmentIndex} out of range (${label} has ${fragments.length} fragment(s))`,
      );
    }
  }
  await commitEdit(
    { op: clause === 'having' ? 'remove-having' : 'remove-where', fragmentIndex },
    ctx,
  );
}
